import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildFormAnswerPayload,
  EntityRefUtil,
  formatManuallyWithUserTimezone,
  FormDTO,
  FormElementKind,
  isSubmissionEditable,
  RecordState,
  TableFormElement,
  type UpdateRepairRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { RevisionHistory } from '@/components/revisions/revision-history';
import { SingleRecordPdfControls } from '@/components/single-record-pdf-controls';
import { Sheet, SheetContent, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { useNativeShare } from '@/hooks/use-native-share';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { cn, initials } from '@/lib/utils';
import { activityKeys } from '@/queries/inspections';
import { getRepairIssuesQueryOptions, issueKeys } from '@/queries/issues';
import { getRepairByIdQueryOptions, getRepairFormQueryOptions, getRepairRevisionsQueryOptions, repairKeys } from '@/queries/repairs';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { LegacySubmissionRenderer } from '@/utils/legacy';
import { getWorkspaceRoute } from '@/utils/routes';

import { ReadOnlyField } from '../common/read-only-field';
import { SaveChangesDialog } from '../common/save-changes-dialog';
import { initializeTableInitialValues } from '../form/elements/table';
import { FormElementsRenderer, FormRendererData } from '../form/form-elements-renderer';
import ActivityIcon from '../icons/activity';
import ArrowLeftIcon from '../icons/arrow-left';
import CloseIcon from '../icons/close';
import { ShareIcon } from '../icons/share';
import WarningIcon from '../icons/warning';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';

export default function RepairDrawer({
  open: openProp,
  onOpenChange,
  repairId,
  noTrigger = false,
  autoTriggerSharePdf = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  repairId?: string;
  noTrigger?: boolean;
  autoTriggerSharePdf?: boolean;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const [open, setOpen] = useState(openProp ?? false);
  const { isAWA } = useIsAWA();
  const { shareFile } = useNativeShare();
  const navigate = useNavigate();
  const [view, setView] = useState<'form' | 'activity'>('form');
  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    setOpen(open);
    if (!open) {
      setView('form');
    }
  };

  const { data: repairData, isFetching: isFetchingRepairData } = useQuery({
    ...getRepairByIdQueryOptions({ repairId, isAWA }),
    enabled: !!repairId && open,
  });

  const { data: repairForm, isFetching: isFetchingRepairFormData } = useQuery({
    ...getRepairFormQueryOptions({ formRevisionId: repairData?.formRevisionId, isAWA }),
    enabled: !!repairId && !!repairData?.assetId,
  });

  const { data: issuesData, isFetching: isFetchingIssuesData } = useQuery(getRepairIssuesQueryOptions({ repairNumber: repairData?.number, isAWA }));

  const { data: revisions, isLoading: isLoadingRevisions } = useQuery({
    ...getRepairRevisionsQueryOptions({ repairId, isAWA }),
    enabled: !!repairId && open && view === 'activity',
  });

  const issueCount = useMemo(() => issuesData?.data?.length || 0, [issuesData]);

  const [printContentReady, setPrintContentReady] = useState(false);
  const repairRef = useRef<HTMLDivElement | null>(null);

  const setRepairRef = useCallback((node: HTMLDivElement | null) => {
    repairRef.current = node;
    if (node) {
      setPrintContentReady(true);
    }
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: repairRef,
    documentTitle: `Repair #${repairData?.number || 'Unknown'} Details`,
    pageStyle: `
      @page { margin: 30px }
      @media print {
        body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
    `,
  });

  const hasAutoTriggeredRef = useRef(false);

  // Auto-trigger share PDF when opening from issue drawer's "Share as PDF" button
  useEffect(() => {
    if (autoTriggerSharePdf && open && repairData && !isFetchingRepairData && printContentReady && !hasAutoTriggeredRef.current) {
      hasAutoTriggeredRef.current = true;
      if (repairData?.document?.url) {
        const url = repairData?.document.url;
        shareFile({ url, filename: 'repair-record.pdf' }).then((shared) => {
          if (!shared) {
            window.open(url, '_blank');
          }
        });
      } else {
        handlePrint();
      }
    }
  }, [autoTriggerSharePdf, open, repairData, isFetchingRepairData, printContentReady, handlePrint, shareFile]);

  // Reset flags when drawer closes
  useEffect(() => {
    if (!open) {
      hasAutoTriggeredRef.current = false;
      setPrintContentReady(false);
    }
  }, [open]);

  const formInstance = useForm<FormRendererData>({
    defaultValues: {
      ...repairForm?.elements.reduce(
        (acc, element) => ({
          ...acc,
          ...(element.kind === FormElementKind.Table
            ? {
                [element.id]: [[...initializeTableInitialValues(element as TableFormElement)]],
              }
            : {
                [element.id]: undefined,
              }),
        }),
        {} as FormRendererData,
      ),
    },
  });

  const isLegacySubmission = useMemo(() => repairData?.legacyData !== null, [repairData?.legacyData]);

  useEffect(() => {
    if (repairData?.data) {
      formInstance.reset(
        repairData.data
          .filter((element): element is NonNullable<typeof element> => element !== null)
          .reduce(
            (acc: FormRendererData, element) => ({ ...acc, [(element as unknown as { elementId: string }).elementId]: element.value }),
            {} as FormRendererData,
          ),
      );
    }
  }, [repairData?.data, formInstance]);

  const toast = useToast();
  const queryClient = useQueryClient();

  // Open editing: any logged-in user can edit an eligible record — no owner/role gate.
  const canEditRecord = !!repairData && !!repairForm && isSubmissionEditable(repairData);
  const isFormDirty = formInstance.formState.isDirty;

  const {
    mutate: saveRepairEdit,
    mutateAsync: saveRepairEditAsync,
    isPending: isSaving,
  } = useMutation({
    mutationFn: async (values: FormRendererData) => {
      if (!repairId) {
        throw new Error('Missing repair id');
      }
      const data = buildFormAnswerPayload(repairForm?.elements ?? [], values);
      const payload: UpdateRepairRequestDTO = { data };
      return await (await (isAWA ? AWAApiV2Service.UpdateRepair(repairId, payload) : ApiService.UpdateRepair(repairId, payload))).json();
    },
    onError: () => {
      toast.error('Unable to save changes. Please try again.');
    },
    onSuccess: () => {
      toast.success('Repair record updated');
      queryClient.invalidateQueries({ queryKey: repairKeys.all });
      // A repair edit changes the linked issue's displayed completed date/by
      // (issue.closedAt ?? repair.repairDate), so refresh the issue queries too.
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
      if (repairData?.assetId) {
        queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(repairData.assetId) });
      }
    },
  });

  const handleInvalidSave = () => {
    toast.error('The record has invalid or incomplete data. Please check.');
    const [firstErrorField] = Object.keys(formInstance.formState.errors);
    if (firstErrorField) {
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => formInstance.formState.isDirty);

  async function closeDrawer() {
    if (!(await confirmSafeToLeave())) return;
    handleOpenChange(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) {
          handleOpenChange(true);
        } else {
          closeDrawer();
        }
      }}>
      {!noTrigger && (
        <SheetTrigger asChild>
          <Button className="h-full rounded-none p-2" variant="ghost">
            View Repair
          </Button>
        </SheetTrigger>
      )}
      <SheetPortal>
        <SheetContent hideCloseIcon side={'right'} className={cn(isAWA ? 'mx-auto h-full w-full sm:max-w-[800px]' : '')}>
          <SheetHeader className="border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white">
            {isAWA ? (
              <>
                <div className="flex items-center gap-2 py-1">
                  <button className="-m-2 p-2" type="button" onClick={() => closeDrawer()}>
                    <ArrowLeftIcon className="size-6" />
                  </button>
                  <SheetTitle className="text-xl">{repairData ? 'Repair record' : 'Create Repair'}</SheetTitle>
                </div>
                {canEditRecord && (
                  <Button
                    variant="ghost"
                    className="text-default-green text-lg font-medium"
                    loading={isSaving}
                    disabled={!isFormDirty || isSaving}
                    onClick={() => formInstance.handleSubmit((values) => saveRepairEdit(values), handleInvalidSave)()}>
                    Save
                  </Button>
                )}
              </>
            ) : (
              <>
                <SheetTitle>{isFetchingRepairData || isFetchingRepairFormData ? 'Loading repair details...' : 'Repair record'}</SheetTitle>
                <div className="flex items-center gap-2">
                  {canEditRecord && (
                    <Button
                      variant="success"
                      loading={isSaving}
                      disabled={!isFormDirty || isSaving}
                      onClick={() => formInstance.handleSubmit((values) => saveRepairEdit(values), handleInvalidSave)()}>
                      Save
                    </Button>
                  )}
                  {repairData && !isLegacySubmission && (
                    <Button
                      variant="grey"
                      size="default"
                      type="button"
                      icon={<ActivityIcon />}
                      onClick={() => setView((prev) => (prev === 'activity' ? 'form' : 'activity'))}>
                      {view === 'activity' ? 'Hide Activity' : 'Show Activity'}
                    </Button>
                  )}
                  {!(isFetchingRepairData || isFetchingRepairFormData) && repairData && (
                    <SingleRecordPdfControls recordType="repair" recordId={repairData.id} documentUrl={repairData.document?.url} />
                  )}
                  <button
                    type="button"
                    className="rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                    onClick={() => closeDrawer()}>
                    <CloseIcon className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </div>
              </>
            )}
          </SheetHeader>

          {(isFetchingRepairData && !repairData) || (isFetchingRepairFormData && !repairForm) || (isFetchingIssuesData && !issuesData) ? (
            <div className="flex flex-col gap-2 px-4 pt-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col gap-1">
                  {[...Array(2)].map((_, i) => (
                    <Skeleton key={i} className={cn('h-4 w-28', i === 1 && 'w-32')} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-end gap-1">
                    {[...Array(2)].map((_, i) => (
                      <Skeleton key={i} className={cn('h-4 w-28', i === 1 && 'w-32')} />
                    ))}
                  </div>
                  <Skeleton className="size-7 rounded-full" />
                </div>
              </div>

              <div className="border-grey-2 animate-pulse border-b-4"></div>

              <div className="flex flex-col gap-4 px-2 py-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className={cn('h-12 w-full rounded px-6', i >= 3 && 'h-32')} />
                ))}
              </div>
            </div>
          ) : (
            <div ref={setRepairRef} className="flex h-full flex-1 flex-col overflow-y-auto">
              {view === 'form' && !!repairData && (
                <div className="flex flex-col gap-1 px-4 pt-4">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex flex-col gap-[2px]">
                      <div>
                        <span className="font-medium">ID: </span> {EntityRefUtil.createRepairRef(repairData.number)}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Issues: </span>
                        <div className="bg-default-light-orange flex items-center gap-1 rounded px-1.5 py-0.5 text-sm text-white">
                          <WarningIcon className="text-default-dark-orange" size={20} />
                          <span className="text-default-dark-orange">{issueCount || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-right">
                      {(() => {
                        const repairedBy = repairData.repairedByUser;
                        const displayUser = repairedBy ?? repairData.contact;
                        const fullName = EntityRefUtil.createUserFullNameRef(displayUser, '-');
                        const avatarUrl = repairedBy?.avatarUrl;
                        return (
                          <>
                            <div className="flex flex-col gap-[2px]">
                              <div className="line-clamp-1 font-medium">{fullName}</div>
                              <div className="text-secondary-text">
                                {isAWA
                                  ? dayjs(repairData.createdAt).format('MMM DD, YYYY hh:mm a')
                                  : formatManuallyWithUserTimezone(new Date(repairData.createdAt), 'MMM DD, YYYY hh:mm a', userTimezone)}{' '}
                              </div>
                            </div>
                            <Avatar>{avatarUrl ? <AvatarImage src={avatarUrl} /> : <AvatarFallback>{initials(fullName)}</AvatarFallback>}</Avatar>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="border-default-blue border-b-4" />
                </div>
              )}

              <div
                className={cn(
                  'scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 flex flex-1 flex-col gap-2 overflow-y-auto',
                  view === 'form' && 'px-6 pt-4 pb-6 print:px-0 print:pt-4 print:pb-4',
                )}>
                {view === 'activity' ? (
                  repairForm && repairData?.asset ? (
                    <RevisionHistory
                      revisions={revisions ?? []}
                      isLoading={isLoadingRevisions}
                      elements={repairForm.elements}
                      form={repairForm.form as FormDTO}
                      asset={repairData.asset}
                    />
                  ) : (
                    <div className="text-grey-5 p-4 text-sm">Loading…</div>
                  )
                ) : (
                  repairData && (
                    <>
                      {!!repairData.asset && (
                        <div className="flex flex-col gap-1">
                          <Label>Asset</Label>
                          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                            {isAWA ? (
                              <span className="text-sm">{EntityRefUtil.createAssetLabel(repairData.asset)}</span>
                            ) : (
                              <Link className="text-default-blue text-sm underline" to={getWorkspaceRoute(`/assets/${repairData.assetId}`)}>
                                {EntityRefUtil.createAssetLabel(repairData.asset)}
                              </Link>
                            )}
                          </div>
                        </div>
                      )}

                      {(() => {
                        const linkedIssue = repairData.issues?.[0];
                        return (
                          <ReadOnlyField label="Issue">
                            {linkedIssue ? (
                              <div className="flex items-center gap-2">
                                {linkedIssue.state === RecordState.Deleted ? (
                                  <span className="text-sm">{linkedIssue.title || `Issue #${linkedIssue.number}`}</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="text-default-blue text-sm underline"
                                    onClick={async () => {
                                      if (!(await confirmSafeToLeave())) return;
                                      handleOpenChange(false);
                                      navigate(getWorkspaceRoute('/issues'), { state: { issueId: linkedIssue.id } });
                                    }}>
                                    {linkedIssue.title || `Issue #${linkedIssue.number}`}
                                  </button>
                                )}
                                {linkedIssue.state === RecordState.Deleted && (
                                  <Badge variant="destructive" size="sm">
                                    Deleted
                                  </Badge>
                                )}
                                {linkedIssue.state === RecordState.Archived && (
                                  <Badge variant="orange" size="sm">
                                    Archived
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm">-</span>
                            )}
                          </ReadOnlyField>
                        );
                      })()}

                      {isLegacySubmission ? (
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        <LegacySubmissionRenderer data={repairData.legacyData as any} />
                      ) : repairForm && repairData.asset ? (
                        <FormProvider {...formInstance}>
                          <form className="flex flex-col gap-2 print:pb-16">
                            <FormElementsRenderer
                              form={repairForm.form as FormDTO}
                              asset={repairData.asset}
                              readonly={!canEditRecord}
                              lockNonEditableElements={canEditRecord}
                              isEditingExistingRecord
                              elements={repairForm.elements}
                            />
                          </form>
                        </FormProvider>
                      ) : undefined}
                    </>
                  )
                )}
              </div>
            </div>
          )}

          {/* AWA FAB for Share as PDF */}
          {isAWA && repairData && (
            <div className="absolute right-4 bottom-4 z-50">
              <Button
                type="button"
                className="bg-default-blue hover:bg-default-dark-blue flex h-[65px] w-[65px] items-center justify-center rounded-full shadow-md"
                onClick={() => {
                  if (repairData.document?.url) {
                    const url = repairData.document.url;
                    shareFile({ url, filename: 'repair-record.pdf' }).then((shared) => {
                      if (!shared) {
                        window.open(url, '_blank');
                      }
                    });
                  } else {
                    handlePrint();
                  }
                }}>
                <ShareIcon size={30} className="text-white" />
              </Button>
            </div>
          )}
        </SheetContent>
      </SheetPortal>
      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={formInstance.handleSubmit(
          (values) =>
            saveRepairEditAsync(values)
              .then(() => {
                formInstance.reset(values);
                settleConfirm(true);
              })
              .catch(() => settleConfirm(false)),
          handleInvalidSave,
        )}
        discardCallback={() => {
          formInstance.reset();
          settleConfirm(true);
        }}
        loading={isSaving}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </Sheet>
  );
}
